"use client";
import ArmyInfoActions from "@/components/list-builder/army-info/ArmyInfoActions";
import { GameSystems, IArmyData, IFleetData } from "@/data/types";
import { useSlugGameSystem } from "@/hooks";
import { useOnScroll } from "@/hooks/framework";
import { Box, Container, Paper, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { type JSX } from "react";
import FleetInfoActions from "../list-builder/warfleets/FleetInfoActions";
import AuthenticationControl from "./AuthenticationControl";
import MenuAdminLink from "./MenuAdminLink";
import { MenuBarShared } from "./MenuBarShared";
import { MenuLinks } from "./MenuLinks";

export type Props = {
  armyData?: IArmyData;
  fleetData?: IFleetData;
  title: string | JSX.Element;
};

export function ArmyInfoMenuBar({ armyData, fleetData, title }: Props) {
  const gameSystem = useSlugGameSystem();
  const ftl = gameSystem.id === GameSystems.ftl;

  const theme = useTheme();
  const mdScreen = useMediaQuery(theme.breakpoints.up("md"));
  const actions = (() => {
    if (fleetData) {
      return <FleetInfoActions fleetId={fleetData.uid} mdScreen={mdScreen} />;
    } else if (armyData) {
      return <ArmyInfoActions mdScreen={mdScreen} armyData={armyData} />;
    }

    return null; // loading / no data yet
  })();

  const fontFamily =
    ftl || GameSystems[armyData?.gameSystemId]?.startsWith("gf") ? "GrimdarkTitle" : "AgeOfFantasyTitle";

  useOnScroll(() => {
    const progress = Math.min(window.scrollY / 200, 1);

    document.body.style.setProperty("--af-scroll-top", String(window.scrollY));
    document.body.style.setProperty("--af-scroll-progress", String(progress));
  });

  const bannerImage = {
    height: {
      xs: "clamp(64px, calc(256px - (var(--af-scroll-top, 0) * 1px)), 256px)",
      md: "clamp(64px, calc(464px - (var(--af-scroll-top, 0) * 1px)), 464px)"
    }
  };

  const lower = (
    <Container
      sx={{
        alignSelf: "flex-end",
        position: "absolute",
        bottom: "calc(6rem * (1 - var(--af-scroll-progress, 0)) + 1rem)",
        left: 0,
        right: 0,
        width: "500px",
        maxWidth: "500px"
      }}
    >
      <Stack
        direction="column"
        sx={{
          justifyContent: "center",
          position: "relative",
          opacity: "calc(1 - var(--af-scroll-progress, 0))"
        }}
      >
        <Typography
          variant="h1"
          sx={{
            pt: 1,
            mb: 4,
            color: "#FFFFFF",
            fontWeight: 400,
            fontFamily: fontFamily,
            textTransform: "uppercase",
            fontSize: "3rem",
            textShadow: "2px 2px 4px rgba(0,0,0,0.75)"
          }}
          align="center"
        >
          {title}
        </Typography>
        <Stack sx={{ maxWidth: "360px", mx: "auto" }}>{actions}</Stack>
      </Stack>
    </Container>
  );

  const appBar = (
    <MenuBarShared
      title={title}
      tabBar={mdScreen && lower}
      imageData={{
        gameSystemId: gameSystem.id,
        bannerImagePath: armyData?.bannerImagePath
      }}
      sx={{
        zIndex: 1,
        ...bannerImage
      }}
    >
      <MenuLinks />
      <MenuAdminLink />
      <Box sx={{ whiteSpace: "nowrap" }}>
        <AuthenticationControl />
      </Box>
    </MenuBarShared>
  );

  return (
    <>
      <Box sx={{ height: { xs: `256px`, md: `464px` } }} />
      <Paper
        className="no-print"
        elevation={1}
        square
        sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 2, overflow: "hidden" }}
      >
        {appBar}
      </Paper>
    </>
  );
}
